import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { parse, format } from 'date-fns';

import timesheetService from '../../../services/TimesheetService';
import {
  getFormattedTableRows,
  isLeaveTimesheetFormDataValid,
  isTimesheetDateInFuture,
  isTimesheetFormDataValid,
  upsertTimesheetRow
} from '../table/helpers';

import { Grid, TextField, Checkbox, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ReactSelect from 'react-select';
import ButtonMenu from './ButtonMenu';
import { toast } from 'react-hot-toast';
import keyPayEmployeeService from '../../../services/KeypayEmployee';
import employeeService from '../../../services/EmployeeService';
import { checkLeaveCategory, isLeaveHoursValid } from './helper';
import useFetchCustomer from '../../CustomerNew/hooks/useFetchCustomer';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1
  },
  textField: {
    width: '233px',
    maxWidth: '100%'
  },
  element: {
    marginRight: theme.spacing(1)
  },
  buttonDiv: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1rem'
  }
}));

const AddTimeDialogContent = ({
  myTimeFormData,
  setMyTimeFormData,
  customerRefList,
  classRefList,
  handleTimesheetAdd,
  handleLeaveTimesheetAdd,
  handleClose,
  takeLeaveOption,
  timesheetSavingOptions,
  setTimesheetSavingOptions,
  setRows,
  setOpen,
  showLeaveBalance,
  setShowLeaveBalance,
  sickLeaveBalance,
  setSickLeaveBalance,
  setProceedFutureDate,
  proceedFutureDate,
  setDateCheckDialogOpen,
  loading,
  setLoading
}) => {
  const [todayDate, setTodayDate] = useState();

  useEffect(() => {
    let todayDate = format(new Date(), 'yyyy-MM-dd');
    setTodayDate(todayDate);
  }, []);

  const classes = useStyles();
  // const [billableCheckboxChecked, setBillableCheckboxChecked] = useState(myTimeFormData.isBillable);
  const [medicalCheckboxChecked, setMedicalCheckboxChecked] = useState(myTimeFormData.medicalStatus);
  const [leaveCategory] = useState([
    'Annual Leave',
    "Personal/Carer's Leave",
    'Leave without pay',
    'Long Service Leave',
    'School',
    'Rostered Day Off',
    'Public Holiday'
  ]);
  const [anchorEl, setAnchorEl] = useState(null);
  const { customerRows } = useFetchCustomer();
  const [selectedClient, setSelectedClient] = useState();

  const handleOptions = async (e, optionValue) => {
    e.preventDefault();
    const actionValue = optionValue || e.currentTarget.innerText || e.currentTarget.textContent || '';
    const normalizedAction = actionValue.trim().toUpperCase();

    const topOption = timesheetSavingOptions[0]?.value || '';

    if (normalizedAction !== topOption.trim().toUpperCase()) {
      const updatedButtonList = [...timesheetSavingOptions].reverse();
      // eslint-disable-next-line no-undef
      localStorage.setItem('timesheetSavingOptions', JSON.stringify(updatedButtonList));
      setTimesheetSavingOptions(updatedButtonList);
    }

    if (normalizedAction === 'SAVE AS DRAFT') {
      if (takeLeaveOption) {
        handleLeaveTimesheetAdd(e);
      } else {
        handleTimesheetAdd(e);
      }
    }

    if (normalizedAction === 'SAVE AND SUBMIT') {
      if (takeLeaveOption) {
        saveAndSubmitLeaveTimesheet();
      } else {
        saveAndSubmitTimesheet();
      }
    }
  };

  //save and submit button: save timesheet and change status to "WithApprover" or "Approved" for timesheet
  const saveAndSubmitTimesheet = async () => {
    const timesheetPayload = isTimesheetFormDataValid(myTimeFormData);
    if (!timesheetPayload) {
      return toast.error('Please fill out all the mandatory data');
    }

    let timesheetDateCheck = false;

    if (!proceedFutureDate) {
      //check if the timesheet date entered is within this week or not
      timesheetDateCheck = isTimesheetDateInFuture(myTimeFormData.date);
    }

    //popup  a dialog
    if (timesheetDateCheck && !proceedFutureDate) {
      setDateCheckDialogOpen(true);
    }

    if (!timesheetDateCheck || proceedFutureDate) {
      // eslint-disable-next-line no-undef
      const formData = new FormData();
      formData.append('timesheetPayload', JSON.stringify(timesheetPayload));
      // eslint-disable-next-line no-undef
      const userId = localStorage.getItem('userId');
      formData.append('userId', userId);

      if (myTimeFormData.timeSheetImageUrl && myTimeFormData.timeSheetImageUrl.length > 0) {
        if (myTimeFormData.timeSheetImageUrl.length > 5) {
          return toast.error('You can upload a maximum of 5 images');
        }
        for (let i = 0; i < myTimeFormData.timeSheetImageUrl.length; i++) {
          formData.append('files', myTimeFormData.timeSheetImageUrl[i]);
        }
      }

      setLoading(true);
      timesheetService
        .saveAndSubmitTimesheet(formData)
        .then(({ data }) => {
          toast.success('Successfully added timesheet');
          let timesheetData = [];
          timesheetData.push(data.data);
          const formattedTimesheet = getFormattedTableRows(timesheetData);
          setRows((currentRows) => upsertTimesheetRow(currentRows, formattedTimesheet[0]));
          setMyTimeFormData({
            // eslint-disable-next-line no-undef
            startTime: localStorage.getItem('startTime') || '07:00',
            // eslint-disable-next-line no-undef
            finishTime: localStorage.getItem('finishTime') || '15:00',
            hour: 8,
            date: todayDate,
            client: '',
            task: '',
            isBillable: false,
            description: '',
            variationNote: '',
            workType: '',
            medicalStatus: false,
            timeSheetImageUrl: ''
          });
          setOpen(false);
        })
        .catch((error) => {
          toast.error(
            error?.response?.data?.error ||
              error?.response?.data?.data?.message ||
              error?.response?.data?.message ||
              'Could not add and submit timesheet'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  //save and submit button: save leave timesheet and change status to "WithApprover" or "Approved" for timesheet
  const saveAndSubmitLeaveTimesheet = async () => {
    const timesheetPayload = isLeaveTimesheetFormDataValid(myTimeFormData);
    if (!timesheetPayload) {
      return toast.error('Please fill out all the mandatory data');
    }

    if (checkLeaveCategory(myTimeFormData.workType)) {
      if (!isLeaveHoursValid(showLeaveBalance, myTimeFormData.hour)) {
        return toast.error(`Not enough leave balance to take ${myTimeFormData.workType}`);
      }
    }

    if (myTimeFormData.workType === "Personal/Carer's Leave") {
      if (!isLeaveHoursValid(sickLeaveBalance, myTimeFormData.hour)) {
        return toast.error(`Not enough leave balance to take ${myTimeFormData.workType}`);
      }
    }

    // eslint-disable-next-line no-undef
    const formData = new FormData();
    formData.append('timesheetPayload', JSON.stringify(timesheetPayload));
    // eslint-disable-next-line no-undef
    const userId = localStorage.getItem('userId');
    formData.append('userId', userId);

    if (myTimeFormData.timeSheetImageUrl && myTimeFormData.timeSheetImageUrl.length > 0) {
      if (myTimeFormData.timeSheetImageUrl.length > 5) {
        return toast.error('You can upload a maximum of 5 images');
      }
      for (let i = 0; i < myTimeFormData.timeSheetImageUrl.length; i++) {
        formData.append('files', myTimeFormData.timeSheetImageUrl[i]);
      }
    }

    setLoading(true);
    timesheetService
      .saveAndSubmitTimesheet(formData)
      .then(({ data }) => {
        toast.success('Successfully added timesheet');
        let timesheetData = [];
        timesheetData.push(data.data);
        const formattedTimesheet = getFormattedTableRows(timesheetData);
        setRows((currentRows) => upsertTimesheetRow(currentRows, formattedTimesheet[0]));
        setMyTimeFormData({
          // eslint-disable-next-line no-undef
          startTime: localStorage.getItem('startTime') || '07:00',
          // eslint-disable-next-line no-undef
          finishTime: localStorage.getItem('finishTime') || '15:00',
          hour: 8,
          date: todayDate,
          client: '',
          task: '',
          isBillable: false,
          description: '',
          variationNote: '',
          workType: '',
          medicalStatus: false,
          timeSheetImageUrl: ''
        });
        setOpen(false);
        setAnchorEl(false);
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.error || error?.response?.data?.message || 'Could not add and submit leave form'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const customerRefOptions = customerRefList.map((c) => {
    return { value: c.name, label: c.name, id: c.value };
  });

  const classRefOptions = classRefList.map((c) => {
    return { value: c.name, label: c.name, id: c.value };
  });

  const leaveCategoryOptions = leaveCategory.map((l) => {
    return { value: l, label: l };
  });

  //difference between starttime and finishtime: hour
  useEffect(() => {
    if (myTimeFormData.startTime && myTimeFormData.finishTime) {
      const startDateMs = parse(myTimeFormData.startTime, 'HH:mm', new Date(myTimeFormData.date));
      const endDateMs = parse(myTimeFormData.finishTime, 'HH:mm', new Date(myTimeFormData.date));

      const diffHr = Math.abs((endDateMs - startDateMs) / (1000 * 60 * 60)).toFixed(1);
      setMyTimeFormData((currentFormData) => ({ ...currentFormData, hour: diffHr }));
      setProceedFutureDate(false);
    }
  }, [
    myTimeFormData.startTime,
    myTimeFormData.finishTime,
    myTimeFormData.date,
    setMyTimeFormData,
    setProceedFutureDate
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startTime' || name === 'finishTime') {
      // eslint-disable-next-line no-undef
      localStorage.setItem(name, value);
    }
    setMyTimeFormData({ ...myTimeFormData, [name]: value });
  };

  //when variation in checked
  const handleVariationChange = (e) => {
    setMyTimeFormData({ ...myTimeFormData, isBillable: e.target.checked });
  };

  //when medical certificate is checked
  const handleMedicalCertificateChange = (e) => {
    setMedicalCheckboxChecked(e.target.checked);
    setMyTimeFormData({
      ...myTimeFormData,
      medicalStatus: e.target.checked,
      timeSheetImageUrl: e.target.checked ? myTimeFormData.timeSheetImageUrl : ''
    });
  };

  const handleTimesheetFile = (e) => {
    if (e.target.files && e.target.files.length > 5) {
      toast.error('You can select a maximum of 5 images');
      e.target.value = ''; // clear the selection
      return;
    }
    setMyTimeFormData({ ...myTimeFormData, timeSheetImageUrl: e.target.files });
  };

  //when client item is clicked :
  const handleClientItem = (selectedItem) => {
    if (customerRows.length) {
      const selectedClientBillableStatus = customerRows.find((c) => c.Id === selectedItem.value);

      const isClientBillable = selectedClientBillableStatus && selectedClientBillableStatus.BillableStatus;

      if (isClientBillable) setSelectedClient(isClientBillable);

      setMyTimeFormData({ ...myTimeFormData, client: selectedItem, isBillable: isClientBillable });
    }
  };

  //when task item is clicked
  const handleTaskItem = (selectedItem) => {
    setMyTimeFormData({ ...myTimeFormData, task: selectedItem });
  };

  //when leave category is choosen
  const handleWorkTypeCategory = async (selectedItem) => {
    if (
      selectedItem === 'Annual Leave' ||
      selectedItem === 'Rostered Day Off' ||
      selectedItem === "Personal/Carer's Leave"
    ) {
      //get keyPayEmployeeId to get leavebalance
      const employeeDetails = await employeeService.getEmployeeDetails();
      const keyPayEmployeeId = employeeDetails.data.data.KeyPayEmployeeId;

      const leaveBalanceResult = await keyPayEmployeeService.getEmployeeLeaveBalance(keyPayEmployeeId);

      const selectedLeave = leaveBalanceResult.data.data.filter((l) => l.leaveCategoryName == selectedItem);

      if (selectedItem !== "Personal/Carer's Leave") {
        const showLeaveBalance = selectedLeave[0].accruedAmount;
        setShowLeaveBalance(showLeaveBalance);
      }
      const sickLeaveBalance = selectedLeave[0].accruedAmount;
      setSickLeaveBalance(sickLeaveBalance);
    }

    setMyTimeFormData({ ...myTimeFormData, workType: selectedItem });
  };

  return (
    <div>
      <form onSubmit={handleTimesheetAdd}>
        <div className={classes.root}>
          <Grid container spacing={3}>
            {/* start time input */}
            <Grid item xs={6}>
              <p>Start Time*</p>
              <TextField
                required={true}
                variant="outlined"
                size="small"
                id="startTime"
                type="time"
                name="startTime"
                value={myTimeFormData.startTime}
                onChange={handleChange}
                className={classes.textField}
                InputLabelProps={{
                  shrink: true
                }}
                inputProps={{
                  step: 300 // 5 min
                }}
              />
            </Grid>

            {/* finish time input */}
            <Grid item xs={6}>
              <p>End Time*</p>
              <TextField
                required={true}
                variant="outlined"
                size="small"
                id="finishTime"
                type="time"
                name="finishTime"
                value={myTimeFormData.finishTime}
                onChange={handleChange}
                className={classes.textField}
                InputLabelProps={{
                  shrink: true
                }}
                inputProps={{
                  step: 300 // 5 min
                }}
              />
            </Grid>

            {/* hour input */}
            <Grid item xs={6}>
              <p>Hour*</p>
              <TextField
                required={true}
                variant="outlined"
                size="small"
                value={myTimeFormData.hour}
                onChange={handleChange}
                className={classes.textField}
                autoFocus
                id="hour"
                // type="text"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                name="hour"
                disabled
              />
            </Grid>

            {/* date input*/}
            <Grid item xs={6}>
              <p>Date*</p>
              <TextField
                required={true}
                variant="outlined"
                size="small"
                id="date"
                name="date"
                type="date"
                value={myTimeFormData.date}
                onChange={handleChange}
                className={classes.textField}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </Grid>

            {/* client input  */}
            {!takeLeaveOption && (
              <Grid item xs={12}>
                <p className={classes.element}>Client*</p>
                {!customerRows.length ? (
                  <CircularProgress />
                ) : (
                  <ReactSelect
                    className="basic-single"
                    classNamePrefix="select"
                    value={customerRefOptions.find((option) => option.id === myTimeFormData.client?.value) || null}
                    isSearchable={true}
                    name="client"
                    options={customerRefOptions}
                    onChange={(selectedOption) => {
                      return handleClientItem({ name: selectedOption.value, value: selectedOption.id });
                    }}
                  />
                )}
              </Grid>
            )}

            {/* activity input */}
            {!takeLeaveOption && (
              <Grid item xs={12}>
                <p className={classes.element}>Activity*</p>
                <ReactSelect
                  className="basic-single"
                  classNamePrefix="select"
                  value={classRefOptions.find((option) => option.id === myTimeFormData.task?.value) || null}
                  isSearchable={true}
                  name="task"
                  options={classRefOptions}
                  // eslint-disable-next-line no-undef
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  onChange={(selectedOption) => {
                    return handleTaskItem({ name: selectedOption.value, value: selectedOption.id });
                  }}
                />
              </Grid>
            )}

            {/* leave category input */}
            {takeLeaveOption && (
              <Grid item xs={12}>
                <p className={classes.element}>Leave Category*</p>
                <ReactSelect
                  maxMenuHeight={250}
                  className="basic-single"
                  classNamePrefix="select"
                  value={leaveCategoryOptions.find((option) => option.value === myTimeFormData.workType) || null}
                  isSearchable={true}
                  name="workType"
                  options={leaveCategoryOptions}
                  // eslint-disable-next-line no-undef
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  onChange={(selectedOption) => {
                    return handleWorkTypeCategory(selectedOption.value);
                  }}
                />
              </Grid>
            )}

            {myTimeFormData.workType === 'Annual Leave' || myTimeFormData.workType === 'Rostered Day Off' ? (
              <Grid item xs={12}>
                Leave Balance : {showLeaveBalance} hours
              </Grid>
            ) : (
              ''
            )}

            {takeLeaveOption && myTimeFormData.workType === "Personal/Carer's Leave" && (
              <Grid item xs={12}>
                <p>Medical Certificate </p>
                <Checkbox
                  checked={medicalCheckboxChecked}
                  id="medicalStatus"
                  name="medicalStatus"
                  onChange={handleMedicalCertificateChange}
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                  style={{ paddingTop: '0px' }}
                />
                {medicalCheckboxChecked && (
                  <Grid item xs={12}>
                    <p>Medical Certificate*</p>
                    <input
                      id="timeSheetImageUrl"
                      type="file"
                      name="timeSheetImageUrl"
                      style={{ paddingTop: '0px' }}
                      onChange={handleTimesheetFile}
                      accept="image/*"
                      multiple
                    />
                  </Grid>
                )}
              </Grid>
            )}

            {/* description input */}
            <Grid item xs={12} style={{ marginBottom: takeLeaveOption ? '1rem' : '' }}>
              <p>Comments*</p>
              <TextField
                id="description"
                type="text"
                name="description"
                variant="outlined"
                multiline
                maxRows={4}
                fullWidth={true}
                value={myTimeFormData.description}
                onChange={handleChange}
              />
            </Grid>

            {/* variation input */}
            {!takeLeaveOption && (
              <Grid item xs={12}>
                <p>Variation</p>
                <Checkbox
                  checked={myTimeFormData.isBillable}
                  id="isBillable"
                  name="isBillable"
                  onChange={handleVariationChange}
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                  style={{ paddingTop: '0px' }}
                  disabled={selectedClient}
                />
                {myTimeFormData.isBillable && (
                  <Grid item xs={12}>
                    <p>Variation Note</p>
                    <TextField
                      id="variationNote"
                      type="text"
                      name="variationNote"
                      variant="outlined"
                      multiline
                      maxRows={4}
                      fullWidth={true}
                      value={myTimeFormData.variationNote}
                      onChange={handleChange}
                    />
                  </Grid>
                )}
              </Grid>
            )}
            {/* variation input end */}

            {/* image to upload */}
            {!takeLeaveOption && (
              <Grid item xs={12}>
                <p>Image</p>
                <input
                  id="timeSheetImageUrl"
                  type="file"
                  name="timeSheetImageUrl"
                  style={{ paddingTop: '0px' }}
                  onChange={handleTimesheetFile}
                  accept="image/*"
                  multiple
                />
              </Grid>
            )}
          </Grid>
        </div>

        {loading && (
          <Grid item xs={12}>
            <p
              style={{
                textAlign: 'right',
                fontSize: '0.9rem',
                marginBottom: '0.5rem',
                fontWeight: '600'
              }}
            >
              Uploading timesheet… Please wait.
            </p>
          </Grid>
        )}
        <div className={classes.buttonDiv}>
          <Button onClick={handleClose} color="secondary" style={{ marginRight: '10px' }} disabled={loading}>
            Cancel
          </Button>
          {timesheetSavingOptions && timesheetSavingOptions.length > 0 && (
            <div
              style={{
                display: 'flex',
                backgroundColor: loading ? 'rgba(0, 0, 0, 0.12)' : '#4040a1',
                borderRadius: '4px'
              }}
            >
              <Button
                onClick={(e) => handleOptions(e, timesheetSavingOptions[0]?.value)}
                color="primary"
                variant="contained"
                type="submit"
                style={{
                  borderRight: loading ? '1px solid rgba(0, 0, 0, 0.26)' : '1px solid white',
                  borderRadius: '4px 0 0 4px'
                }}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {timesheetSavingOptions.length ? timesheetSavingOptions[0].value : ''}
              </Button>
              <ButtonMenu
                buttonList={timesheetSavingOptions.filter((t, index) => index !== 0)}
                handleOptions={handleOptions}
                setAnchorEl={setAnchorEl}
                anchorEl={anchorEl}
                style={{ backgroundColor: 'blue' }}
                disabled={loading}
              />
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default AddTimeDialogContent;

AddTimeDialogContent.propTypes = {
  myTimeFormData: PropTypes.shape({
    startTime: PropTypes.string,
    finishTime: PropTypes.string,
    hour: PropTypes.any,
    date: PropTypes.any,
    client: PropTypes.any,
    task: PropTypes.any,
    workType: PropTypes.string,
    isBillable: PropTypes.bool,
    description: PropTypes.string,
    variationNote: PropTypes.string,
    medicalStatus: PropTypes.bool,
    timeSheetImageUrl: PropTypes.any
  }),
  setMyTimeFormData: PropTypes.func,
  customerRefList: PropTypes.array,
  classRefList: PropTypes.array,
  handleTimesheetAdd: PropTypes.func,
  handleLeaveTimesheetAdd: PropTypes.func,
  handleClose: PropTypes.func,
  takeLeaveOption: PropTypes.bool,
  timesheetSavingOptions: PropTypes.array,
  setTimesheetSavingOptions: PropTypes.func,
  setRows: PropTypes.func,
  setOpen: PropTypes.func,
  showLeaveBalance: PropTypes.number,
  setShowLeaveBalance: PropTypes.func,
  sickLeaveBalance: PropTypes.number,
  setSickLeaveBalance: PropTypes.func,
  setProceedFutureDate: PropTypes.func,
  proceedFutureDate: PropTypes.bool,
  setDateCheckDialogOpen: PropTypes.func,
  loading: PropTypes.bool,
  setLoading: PropTypes.func
};
